/**
 * ShiftTimePicker -- lets users select predefined shift periods or custom times.
 * Preset/custom modes show a circular clock dial for visual time selection.
 * Manual mode provides clock-in/clock-out buttons (real-time punch).
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import ClockDial from './ClockDial';

export type ShiftMode = 'evening' | 'extended' | 'weekend' | 'custom';

interface ShiftTimePickerProps {
  mode: ShiftMode;
  onModeChange: (mode: ShiftMode) => void;
  customStart: string;
  customEnd: string;
  onCustomStartChange: (val: string) => void;
  onCustomEndChange: (val: string) => void;
}

const MODES: { id: ShiftMode; label: string }[] = [
  { id: 'evening', label: '1800–0600' },
  { id: 'extended', label: '1800–0900' },
  { id: 'weekend', label: 'Weekend' },
  { id: 'custom', label: 'Custom' },
];

/**
 * Get the default preset start/end as datetime-local strings for a mode.
 */
function getPresetDatetimeLocal(mode: ShiftMode): { start: string; end: string } | null {
  if (mode === 'custom') return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (mode === 'evening') {
    const start = new Date(today);
    start.setHours(18, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    end.setHours(6, 0, 0, 0);
    return { start: toDatetimeLocal(start), end: toDatetimeLocal(end) };
  }

  if (mode === 'extended') {
    const start = new Date(today);
    start.setHours(18, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    end.setHours(9, 0, 0, 0);
    return { start: toDatetimeLocal(start), end: toDatetimeLocal(end) };
  }

  if (mode === 'weekend') {
    const dayOfWeek = today.getDay();
    const saturday = new Date(today);
    if (dayOfWeek === 6) {
      // Saturday
    } else if (dayOfWeek === 0) {
      saturday.setDate(today.getDate() - 1);
    } else {
      saturday.setDate(today.getDate() + (6 - dayOfWeek));
    }
    saturday.setHours(9, 0, 0, 0);
    const monday = new Date(saturday);
    monday.setDate(saturday.getDate() + 2);
    monday.setHours(6, 0, 0, 0);
    return { start: toDatetimeLocal(saturday), end: toDatetimeLocal(monday) };
  }

  return null;
}

/** Convert Date to datetime-local string (YYYY-MM-DDTHH:mm) */
function toDatetimeLocal(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Compute start/end ISO strings for a given mode.
 */
export function computeShiftTimes(
  mode: ShiftMode,
  customStart: string,
  customEnd: string,
): { start: string; end: string } | null {
  if (mode === 'custom') {
    if (!customStart || !customEnd) return null;
    return { start: new Date(customStart).toISOString(), end: new Date(customEnd).toISOString() };
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (mode === 'evening') {
    const start = new Date(today);
    start.setHours(18, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    end.setHours(6, 0, 0, 0);
    return { start: start.toISOString(), end: end.toISOString() };
  }

  if (mode === 'extended') {
    const start = new Date(today);
    start.setHours(18, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    end.setHours(9, 0, 0, 0);
    return { start: start.toISOString(), end: end.toISOString() };
  }

  if (mode === 'weekend') {
    const dayOfWeek = today.getDay();
    const saturday = new Date(today);
    if (dayOfWeek === 6) {
      // Saturday
    } else if (dayOfWeek === 0) {
      saturday.setDate(today.getDate() - 1);
    } else {
      saturday.setDate(today.getDate() + (6 - dayOfWeek));
    }
    saturday.setHours(9, 0, 0, 0);
    const monday = new Date(saturday);
    monday.setDate(saturday.getDate() + 2);
    monday.setHours(6, 0, 0, 0);
    return { start: saturday.toISOString(), end: monday.toISOString() };
  }

  return null;
}

/**
 * Split a datetime-local value into date and time parts.
 */
function splitDatetime(val: string): { date: string; time: string } {
  if (!val) return { date: '', time: '' };
  const [date, time] = val.split('T');
  return { date: date || '', time: time || '' };
}

/**
 * Combine separate date and time strings into a datetime-local value.
 */
function combineDatetime(date: string, time: string): string {
  if (!date || !time) return '';
  return `${date}T${time}`;
}

/** Extract minutes-since-midnight from a datetime-local string. */
function datetimeToMinutes(val: string): number {
  if (!val) return 0;
  const parts = val.split('T');
  if (!parts[1]) return 0;
  const [h, m] = parts[1].split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Format a date string as short label like "Wed, Mar 11" */
function formatDateLabel(val: string): string {
  if (!val) return '';
  const parts = val.split('T');
  const dateStr = parts[0];
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/** Replace the time portion of a datetime-local string, keeping the date. */
function setTimeInDatetime(datetimeLocal: string, minutes: number): string {
  if (!datetimeLocal) return '';
  const parts = datetimeLocal.split('T');
  if (!parts[0]) return '';
  return `${parts[0]}T${minutesToTimeStr(minutes)}`;
}

/** Convert minutes-since-midnight to "HH:mm" string. */
function minutesToTimeStr(minutes: number): string {
  const h = Math.floor((((minutes % 1440) + 1440) % 1440) / 60);
  const m = ((minutes % 60) + 60) % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Add days to a "YYYY-MM-DD" date string and return a new "YYYY-MM-DD" string. */
function addDaysToDate(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() + days);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Convert minutes-since-midnight to angle on the 24h clock face (0–360). */
function minutesToAngle(minutes: number): number {
  return ((((minutes % 1440) + 1440) % 1440) / 1440) * 360;
}

/** Compute the day difference between two "YYYY-MM-DD" date strings. */
function diffDays(dateA: string, dateB: string): number {
  if (!dateA || !dateB) return 0;
  const a = new Date(dateA + 'T12:00:00').getTime();
  const b = new Date(dateB + 'T12:00:00').getTime();
  return Math.round((b - a) / 86_400_000);
}

/** Compute duration in minutes between two datetime-local strings. */
function computeDurationMinutes(startDT: string, endDT: string): number {
  if (!startDT || !endDT) return 0;
  const s = new Date(startDT).getTime();
  const e = new Date(endDT).getTime();
  if (isNaN(s) || isNaN(e)) return 0;
  const diff = e - s;
  return Math.max(0, Math.round(diff / 60_000));
}

export default function ShiftTimePicker({
  mode,
  onModeChange,
  customStart,
  customEnd,
  onCustomStartChange,
  onCustomEndChange,
}: ShiftTimePickerProps) {
  // Extra-days offset for the end handle: 0 = up to 24h, 1 = 24–48h.
  // Tracked via wrap detection when the end handle crosses the start handle.
  const [extraDays, setExtraDays] = useState(0);
  const prevEndSweepRef = useRef<number | null>(null);

  // When mode changes to a preset, populate the custom start/end fields
  // and reset extraDays appropriately.
  useEffect(() => {
    if (mode === 'custom') return;
    const preset = getPresetDatetimeLocal(mode);
    if (preset) {
      onCustomStartChange(preset.start);
      onCustomEndChange(preset.end);
      // Weekend shifts span ~45h; evening/extended are <24h
      setExtraDays(mode === 'weekend' ? 1 : 0);
      prevEndSweepRef.current = null;
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clock dial handlers: update time portion, auto-switch to custom,
  // and auto-adjust dates using extraDays lap tracking.
  const handleClockStartChange = useCallback(
    (minutes: number) => {
      if (mode !== 'custom') {
        onModeChange('custom');
      }
      const updated = setTimeInDatetime(customStart, minutes);
      if (!updated) return;
      onCustomStartChange(updated);

      // Recompute end date using current extraDays
      const startDate = splitDatetime(updated).date;
      const endMins = datetimeToMinutes(customEnd);
      const endTimeStr = splitDatetime(customEnd).time || '06:00';
      const crossesMidnight = endMins < minutes ? 1 : 0;
      const totalOffset = extraDays + crossesMidnight;
      if (totalOffset > 0) {
        onCustomEndChange(combineDatetime(addDaysToDate(startDate, totalOffset), endTimeStr));
      } else {
        onCustomEndChange(combineDatetime(startDate, endTimeStr));
      }
    },
    [mode, customStart, customEnd, extraDays, onCustomStartChange, onCustomEndChange, onModeChange],
  );

  const handleClockEndChange = useCallback(
    (minutes: number) => {
      if (mode !== 'custom') {
        onModeChange('custom');
      }

      const startMins = datetimeToMinutes(customStart);
      const startAngle = minutesToAngle(startMins);
      const newEndAngle = minutesToAngle(minutes);
      // Sweep = angular distance from start to end going clockwise
      const newSweep = (((newEndAngle - startAngle) % 360) + 360) % 360;

      let newExtraDays = extraDays;
      if (prevEndSweepRef.current !== null) {
        const prevSweep = prevEndSweepRef.current;
        // Forward wrap: end crossed start going clockwise → add a lap
        if (prevSweep > 270 && newSweep < 90) {
          newExtraDays = Math.min(extraDays + 1, 1);
        }
        // Backward wrap: end crossed start going counter-clockwise → remove a lap
        if (prevSweep < 90 && newSweep > 270) {
          newExtraDays = Math.max(extraDays - 1, 0);
        }
      }
      prevEndSweepRef.current = newSweep;
      if (newExtraDays !== extraDays) setExtraDays(newExtraDays);

      const startDate = splitDatetime(customStart).date;
      const timeStr = minutesToTimeStr(minutes);
      const crossesMidnight = minutes < startMins ? 1 : 0;
      const totalOffset = newExtraDays + crossesMidnight;

      if (totalOffset > 0) {
        onCustomEndChange(combineDatetime(addDaysToDate(startDate, totalOffset), timeStr));
      } else {
        onCustomEndChange(combineDatetime(startDate, timeStr));
      }
    },
    [mode, customStart, extraDays, onCustomEndChange, onModeChange],
  );

  const startParts = splitDatetime(customStart);
  const endParts = splitDatetime(customEnd);
  const startMinutes = datetimeToMinutes(customStart);
  const endMinutes = datetimeToMinutes(customEnd);
  const durationMinutes = computeDurationMinutes(customStart, customEnd);

  return (
    <div className="w-full">
      <div className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-2">Shift Time</div>

      {/* Mode selector buttons */}
      <div className="flex flex-wrap gap-1.5">
        {MODES.map((m) => {
          const active = mode === m.id;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => onModeChange(m.id)}
              className={`
                px-3 py-1.5 text-xs font-medium rounded-lg border
                transition-all duration-150 cursor-pointer touch-manipulation whitespace-nowrap
                ${
                  active
                    ? 'border-action-primary text-action-primary bg-action-primary/10 font-bold'
                    : 'border-edge-default text-content-secondary bg-surface-raised hover:border-edge-strong hover:text-content-primary'
                }
              `}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      {/* Content area */}
      <div className="mt-3">
        <div className="space-y-3">
          {/* Clock dial */}
          <ClockDial
            startMinutes={startMinutes}
            endMinutes={endMinutes}
            durationMinutes={durationMinutes}
            onStartChange={handleClockStartChange}
            onEndChange={handleClockEndChange}
            startDateLabel={formatDateLabel(customStart)}
            endDateLabel={formatDateLabel(customEnd)}
            startDateValue={startParts.date}
            endDateValue={endParts.date}
            onStartDateChange={(date: string) => {
              if (mode !== 'custom') onModeChange('custom');
              onCustomStartChange(combineDatetime(date, startParts.time || '18:00'));
            }}
            onEndDateChange={(date: string) => {
              if (mode !== 'custom') onModeChange('custom');
              onCustomEndChange(combineDatetime(date, endParts.time || '06:00'));
              // Recompute extraDays from the new date difference
              const startDate = splitDatetime(customStart).date;
              const dayDiff = diffDays(startDate, date);
              const crossesMidnight = endMinutes < startMinutes ? 1 : 0;
              setExtraDays(Math.max(0, Math.min(1, dayDiff - crossesMidnight)));
              prevEndSweepRef.current = null;
            }}
          />
        </div>
      </div>
    </div>
  );
}
